import UE from '@msgai/passport-authz-js';
import { buildPermissionMap, type PermissionMap } from './permission-map';

export function safeCall<T>(fn: (() => T) | undefined): T | null {
  if (typeof fn !== 'function') return null;
  try {
    return fn();
  } catch {
    return null;
  }
}

/**
 * Whether `@msgai/passport-authz-js` has been seeded with an identity.
 * `UE.exists()` is the canonical "is the lib ready?" check (matches
 * aistudio's `plugin-passport.js`).
 */
export function isPassportSeeded(): boolean {
  return safeCall(() => UE.exists()) === true;
}

/**
 * Default resource path used to probe for SuperAdmin / OrgAdmin status.
 * Mirrors aistudio's `studio/*\/*` convention
 * (`client/services/passportUtil.js:12-17`). Consumers outside the studio
 * surface (e.g. embedded apps that author their own passport policies)
 * should pass a custom path.
 */
export const DEFAULT_ADMIN_PROBE_PATH = 'studio/*/*';

/**
 * Super Admin shortcut — aistudio uses this exact call to detect Super
 * Admin regardless of resource-specific policies
 * (`client/services/passportUtil.js:13`). Returns null when UE isn't
 * seeded so callers can distinguish "not ready" from "denied".
 *
 * @param probePath Resource path used as the wildcard probe.
 *   Defaults to `DEFAULT_ADMIN_PROBE_PATH` (`studio/*\/*`).
 */
export function isPassportSuperAdmin(probePath: string = DEFAULT_ADMIN_PROBE_PATH): boolean | null {
  if (!isPassportSeeded()) return null;
  try {
    return UE.hasAccess(probePath, 'SuperAdmin');
  } catch {
    return null;
  }
}

/**
 * Super Admin by **role assignment** rather than policy evaluation:
 * `UE.isSuperAdmin()` checks `roles[].userRoles[].roleId === SUPER_ADMIN_ROLE_ID`
 * (`1`) directly, with no resource/action matching.
 *
 * Distinct from {@link isPassportSuperAdmin}, which probes
 * `hasAccess('studio/*\/*', 'SuperAdmin')` and therefore depends on the
 * backend-issued policy documents actually granting an action named
 * `SuperAdmin` on that path. Use this one when mirroring a host check that
 * keys off the role id — e.g. aistudio's `useMetricPermissions()`, which
 * compares `store.state.userRole.roleId` against the same constant — so the
 * remote grants access to exactly the same users the host does.
 *
 * Returns null when UE isn't seeded, so callers can tell "not ready" from
 * "denied" (same contract as the other probes here).
 */
export function hasPassportSuperAdminRole(): boolean | null {
  if (!isPassportSeeded()) return null;
  // Present at runtime (`user-entitlement.js`) but absent from the package's
  // shipped `.d.ts` on 0.3.8, which declares `isPassportAdmin` instead —
  // narrow structurally rather than augmenting the module, and treat a
  // version that genuinely lacks it as "unknown" rather than "denied".
  const ue = UE as unknown as { isSuperAdmin?: () => boolean };
  if (typeof ue.isSuperAdmin !== 'function') return null;
  return safeCall(() => ue.isSuperAdmin?.() === true);
}

export function isPassportOrgAdmin(probePath: string = DEFAULT_ADMIN_PROBE_PATH): boolean | null {
  if (!isPassportSeeded()) return null;
  try {
    return UE.hasAccess(probePath, 'OrgAdmin');
  } catch {
    return null;
  }
}

/**
 * Build a permission map by calling `UE.hasAccess(accessResourcePath, perm)`
 * for each permission. Returns null when UE isn't seeded so callers can
 * fall back to a tag-rule resolver. Individual `hasAccess` throws are
 * treated as deny.
 *
 * Admin-tier overlays (granting clone/override based on role tags when the
 * backend policy doesn't list them) are intentionally NOT applied here —
 * keep that overlay in app-side code where `ADMIN_TIER_PERMISSIONS` lives.
 */
export function resolveAuthzPermissions<P extends string>(
  perms: readonly P[],
  accessResourcePath: string
): PermissionMap<P> | null {
  if (!isPassportSeeded()) return null;
  const map = buildPermissionMap(perms, false);
  for (const p of perms) {
    try {
      map[p] = UE.hasAccess(accessResourcePath, p);
    } catch {
      map[p] = false;
    }
  }
  return map;
}

export interface SeedPassportOptions {
  orgId?: number;
  botId?: string;
}

/**
 * Seed `@msgai/passport-authz-js` with a host-supplied passport user. Matches
 * the shape aistudio uses in `client/views/Bots/store/mutations.js:28` —
 * `{ user, currentOrgId, currentBotId }`. Silent on failure; callers fall
 * back to a tag-rule resolver if seeding doesn't take.
 *
 * `orgId` precedence: `options.orgId` (if a number) → `user.orgId` (if a
 * number) → `-1`. Note that `0` is accepted as a valid orgId here, unlike
 * the truthy-OR pattern in some host shells — callers that need to drop
 * `0` must pre-filter. Consumers in embedded mode that have a separate
 * `bot.orgId` from the host should forward it explicitly as
 * `options.orgId`; the library has no concept of a host "bot".
 */
export function seedPassportEntity(
  user: Record<string, unknown> | null,
  options: SeedPassportOptions = {}
): void {
  if (!user) return;
  try {
    if (typeof UE.setEntity !== 'function') return;
    const orgId =
      typeof options.orgId === 'number'
        ? options.orgId
        : typeof (user as { orgId?: unknown }).orgId === 'number'
          ? (user as { orgId: number }).orgId
          : -1;
    const botId = typeof options.botId === 'string' ? options.botId : '';
    UE.setEntity({ user, currentOrgId: orgId, currentBotId: botId });
  } catch {
    // Silent — consumers fall back to tag-rule.
  }
}

/**
 * Clears whatever entity `seedPassportEntity` most recently seeded.
 *
 * `@msgai/passport-authz-js` exposes no dedicated clear/reset call — `UE.exists()`
 * checks for a seeded `user`, so `setEntity({})` (no `user` key) is the one
 * documented-shape call that leaves it seedless: `UE.setEntity(null)` and
 * `UE.setEntity(undefined)` both throw reading `.user` off the argument,
 * verified against the installed 0.3.8 package. `isPassportSeeded()` returns
 * `false` immediately afterward.
 *
 * Call this whenever the host's shared passport state goes empty (e.g.
 * logout) — leaving the previous entity seeded would let permission checks
 * keep passing for a user who is no longer logged in.
 */
export function clearPassportEntity(): void {
  try {
    if (typeof UE.setEntity !== 'function') return;
    UE.setEntity({});
  } catch {
    // Silent — matches seedPassportEntity's failure mode.
  }
}
